import { t } from 'ttag';
import {
  CompleteSequenceDocument,
  CompleteSequenceMutation,
  LearnerTrainingStatus
} from '~/data';
import { client } from '~/data/client';
import { markAppReload } from '~/lib/reloadDiagnostics';
import { reloadPage } from '~/lib/reloadPage';
import { attemptRecovery } from '~/lib/recoveryGuard';
import { SliceCreator } from '../TrainingState';
import { NavigationSlice as Slice, Sequence } from './NavigationSlice';
import { notifyLearnerSequenceNavigation } from '~/shell/PostMessage';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';

export const createNavigationSlice: SliceCreator<Slice> = (set, get) => ({
  open: false,
  learnerTrainingId: '',
  chapterSequenceId: '',
  title: '',
  duration: 0,
  regionName: '',
  activeItemPosition: -1,
  highestUnlockedPosition: -1,
  items: [],
  status: LearnerTrainingStatus.Unassigned,
  skip: false,
  shouldSaveVideoWatchedSeconds: false,
  autoSaveVideoWatchedSecondsIntervalInSeconds: null,
  player: 'standard',
  scorePercent: 0,
  lastAttemptScorePercent: null,
  scorePassingPercent: 0,
  attemptStatus: '',
  attemptLimit: null,
  attemptsRemaining: null,
  policyAcknowledgement: false,
  policyAcknowledgementSignature: false,
  subtitles: false,

  activateSequence: id => {
    const { items, learnerTrainingId } = get();
    const item = items.find(i => i.kind === 'sequence' && i.id === id);
    if (!item) throw Error(`Sequence with id ${id} not found`);
    // if (
    //   item.position > highestUnlockedPosition &&
    //   environment !== Environments.Dev
    // ) {
    //   throw Error('Sequence locked');
    // }
    notifyLearnerSequenceNavigation(learnerTrainingId, id);
    set({ activeItemPosition: item.position });
  },

  unlockSequence: id => {
    const item = get().items.find(i => i.kind === 'sequence' && i.id === id);
    if (!item) throw Error(`Sequence with id ${id} not found`);
    set({
      highestUnlockedPosition: Math.max(
        get().highestUnlockedPosition,
        item.position
      )
    });
  },

  prev: async () => {
    const { items, activeItemPosition } = get();
    let index = items.findIndex(
      i => i.kind === 'sequence' && i.position === activeItemPosition
    );

    if (index < 0) {
      // This changes the status only on the UI so that we can get past the onboarding process.
      // Moving past the first sequence will update the status on the server
      set({ status: LearnerTrainingStatus.InProgress });

      for (index++; index < items.length; index++) {
        const item = items[index];
        if (item.kind === 'sequence') {
          set({
            highestUnlockedPosition: Math.max(
              get().highestUnlockedPosition,
              item.position
            )
          });
          return item.id;
        }
      }
    }

    const sequences = items.filter(
      i => i.kind === 'sequence' && i.position < activeItemPosition
    );

    const sequence = sequences[Object.keys(sequences).length - 1];

    if (!sequence) {
      return 'onboarding';
    }

    set({
      highestUnlockedPosition: Math.max(
        get().highestUnlockedPosition,
        sequence.position
      )
    });

    return sequence.id;
  },

  next: async () => {
    const { items, activeItemPosition, learnerTrainingId } = get();

    // let index = items.findIndex(
    //   i => i.kind === 'sequence' && i.position === activeItemPosition
    // );

    //it is HOT fix for the issue with the activeItemPosition if we will have duplicates of the same position

    let index = -1;
    for (let i = items.length - 1; i >= 0; i--) {
      if (
        items[i].kind === 'sequence' &&
        items[i].position === activeItemPosition
      ) {
        index = i;
        break;
      }
    }

    if (index < 0) {
      // This changes the status only on the UI so that we can get past the onboarding process.
      // Moving past the first sequence will update the status on the server
      set({ status: LearnerTrainingStatus.InProgress });

      for (index++; index < items.length; index++) {
        const item = items[index];
        if (item.kind === 'sequence') {
          set({
            highestUnlockedPosition: Math.max(
              get().highestUnlockedPosition,
              item.position
            )
          });
          return item.id;
        }
      }
    }

    const result = await client.mutate<CompleteSequenceMutation>({
      mutation: CompleteSequenceDocument,
      variables: {
        learnerTrainingUuid: learnerTrainingId,
        chapterSequenceId: (items[index] as Sequence).chapterSequenceId
      }
    });

    let shouldReturnCurrentActivePosition = false;

    if (
      result?.data?.completeChapterSequence?.richErrors &&
      result?.data?.completeChapterSequence?.richErrors.length > 0
    ) {
      for (const richError of result.data.completeChapterSequence.richErrors) {
        if (richError.type === 'learner_training_completed_too_fast') {
          set({
            shouldDisplayTimerStillRunningModal:
              richError?.data?.should_display_timer_still_running_modal,
            remainingDurationInMinutesForCourseTimers:
              richError?.data?.remaining_duration_in_minutes_for_course_timers,
            remainingDurationInSecondsForCourseTimers:
              richError?.data?.remaining_duration_in_seconds_for_course_timers
          });

          shouldReturnCurrentActivePosition = true;
        }
      }
    }

    if (
      result?.data?.completeChapterSequence?.errors &&
      result?.data?.completeChapterSequence?.errors.length > 0
    ) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: result.data.completeChapterSequence.errors[0]
      });

      shouldReturnCurrentActivePosition = true;
    }

    if (shouldReturnCurrentActivePosition) {
      return (
        items.find(
          i => i.kind === 'sequence' && i.position === activeItemPosition
        )?.id || ''
      );
    }

    const nextId =
      result.data?.completeChapterSequence?.learnerTraining?.chapterSequenceId;
    const learnerTraining =
      result.data?.completeChapterSequence?.learnerTraining;
    if (learnerTraining) {
      set({
        scorePercent: learnerTraining.scorePercent ?? 0,
        lastAttemptScorePercent:
          learnerTraining.lastAttemptScorePercent ?? null,
        attemptStatus: learnerTraining.attemptStatus || '',
        attemptLimit: learnerTraining.attemptLimit ?? null,
        attemptsRemaining: learnerTraining.attemptsRemaining ?? null
      });
    }

    if (result.data?.completeChapterSequence?.learnerTraining?.certificateUrl) {
      set({
        certificateUrl:
          result.data?.completeChapterSequence?.learnerTraining?.certificateUrl
      });
    }

    if (
      result.data?.completeChapterSequence?.learnerTraining
        ?.featureShowCertificate
    ) {
      set({
        featureShowCertificate:
          result.data?.completeChapterSequence?.learnerTraining
            ?.featureShowCertificate
      });
    }

    if (
      learnerTraining?.scorePassingPercent !== null &&
      learnerTraining?.scorePassingPercent !== undefined
    ) {
      set({
        scorePassingPercent: learnerTraining.scorePassingPercent
      });
    }

    const remainingDurationInMinutesForCourseTimers =
      result.data?.completeChapterSequence?.learnerTraining
        ?.remainingDurationInMinutesForCourseTimers;

    if (remainingDurationInMinutesForCourseTimers !== undefined) {
      set({ remainingDurationInMinutesForCourseTimers });
    }

    if (
      learnerTraining?.remainingDurationInSecondsForCourseTimers !== undefined
    ) {
      set({
        remainingDurationInSecondsForCourseTimers:
          learnerTraining.remainingDurationInSecondsForCourseTimers
      });
    }

    if (!nextId) {
      if (
        result.data?.completeChapterSequence?.learnerTraining?.attemptStatus ===
        'failed'
      ) {
        return 'passfail';
      }

      // If there is no id, that means the training is complete. We can send the user to the feedback form!
      // But first, change the status in state...
      set({ status: LearnerTrainingStatus.Completed });
      return 'feedback';
    }
    const sequence = items.find(
      i => i.kind === 'sequence' && i.chapterSequenceId === nextId
    );

    if (!sequence) {
      // A publish moved the learner to a newer version than the one this player loaded.
      const recovery = {
        learnerTrainingId: get().learnerTrainingId,
        chapterSequenceId: nextId
      };

      if (
        attemptRecovery(
          'next-slide-reload',
          `${recovery.learnerTrainingId}.${nextId}`
        )
      ) {
        markAppReload('next-slide-not-in-course', recovery);
        reloadPage();
        return new Promise<string>(() => undefined);
      }

      markAppReload('next-slide-recovery-stopped', recovery);
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: t`This course was updated. Go back to My Courses and open it again to continue.`
      });
      return (
        items.find(
          i => i.kind === 'sequence' && i.position === activeItemPosition
        )?.id || ''
      );
    }
    set({
      highestUnlockedPosition: Math.max(
        get().highestUnlockedPosition,
        sequence.position
      )
    });

    return sequence.id;
  },

  toggleNav: open => set({ open: open === undefined ? !get().open : open }),

  remainingDurationInMinutesForCourseTimers: null,
  shouldDisplayTimerStillRunningModal: false,

  hideTimerStillRunningModal: () =>
    set({ shouldDisplayTimerStillRunningModal: false }),

  getFirstChapterFirstSequenceId: () => {
    const { items } = get();

    if (items.length === 0) return null;

    const firstChapter = items.find(item => item.kind === 'chapter');

    if (!firstChapter) return null;

    const firstSequence = items.find(
      item => item.kind === 'sequence' && item.chapterId === firstChapter.id
    );

    return firstSequence?.id || null;
  }
});
